import { useMemo, useRef, useState } from "react";

import { Select } from "antd";
import { AxiosError } from "axios";
import styled from "styled-components";

import { NamedOption, PlaceLocation } from "api/map";

import { BaseButton } from "components/base";
import FieldErrorMessage from "components/base/FieldErrorMessage";

import { JumpActionRow, jumpPanelStyles } from "./jumpPanelLayout";
import {
  CASCADE_FIELDS,
  CascadeFieldName,
  JumpToLocationSelection,
  fieldsClearedBy
} from "./jumpToLocationGrids";
import useJumpToLocationOptions, { OptionLevel } from "./useJumpToLocationOptions";

// Every level of every survey system, empty. One of them is a region and the rest are
// whichever levels that region's form has, so the unused ones simply stay null.
const emptyCascade = (): JumpToLocationSelection => {
  const levels = {} as Record<CascadeFieldName, Nullable<string>>;
  CASCADE_FIELDS.forEach((field) => {
    levels[field] = null;
  });
  return { region: null, ...levels };
};

const REGION_TEST_ID = "jump-state-select";
const REGION_LABEL = "State";

const LOCATION_UNAVAILABLE = "Location data unavailable";
const UNABLE_TO_FIND = "Unable to find location";
// A region the service names but this build has no form for. Nothing can be drawn under
// it, so it says why rather than leaving the panel looking half-loaded.
const NO_FORM_FOR_REGION = "No survey levels for this region";

// The lookup answers 503 when an environment has no grid data loaded. That is not a bad
// search, so the cascade says so instead of blaming what the user picked.
const REFERENCE_UNAVAILABLE_STATUS = 503;

interface LevelStatus {
  disabled: boolean;
  loading: boolean;
  message: Nullable<string>;
  canRetry: boolean;
}

/**
 * Turns one cascade level into what its select should show. Every reason to be unusable
 * has its own message, and none of them leaves a spinner running.
 */
function describeLevel(
  level: OptionLevel,
  name: string,
  parentReady: boolean,
  emptyMessage: string
): LevelStatus {
  const idle = { disabled: true, loading: false, message: null, canRetry: false };

  if (!parentReady) return idle;
  // isLoading only covers the first request, so a Retry after a failure is read from
  // isFetching. A list that already has options keeps them while it refreshes, rather
  // than closing the select under the user.
  if (level.isLoading || (level.isFetching && !level.options.length)) {
    return { ...idle, loading: true };
  }
  if (level.isUnavailable) return { ...idle, message: LOCATION_UNAVAILABLE };
  if (level.isError) return { ...idle, message: `Couldn't load ${name}`, canRetry: true };
  if (!level.options.length) return { ...idle, message: emptyMessage };

  return { disabled: false, loading: false, message: null, canRetry: false };
}

function toSelectOption(option: NamedOption) {
  return { value: option.id, label: option.label };
}

interface CascadeFieldProps {
  testId: string;
  placeholder: string;
  value: Nullable<string>;
  options: unknown[];
  status: LevelStatus;
  onRetry: () => void;
  onChange: (value: string) => void;
}

function CascadeField({
  testId,
  placeholder,
  value,
  options,
  status,
  onRetry,
  onChange
}: CascadeFieldProps) {
  // A stack of selects shows a column of bare values, so a level that has one keeps its
  // placeholder as a label above the value. An empty level reads well as it is, and the
  // label keeps its space there so the levels below it stay where they are.
  const hasValue = Boolean(value);

  return (
    <div className={hasValue ? "cascade-field filled" : "cascade-field"}>
      <span className="field-label">{placeholder}</span>
      <CascadeSelect
        data-testid={testId}
        showSearch
        // Every level can be put back to unchosen, the region included. Clearing one
        // forgets the levels under it, the same way choosing a new value does.
        allowClear
        optionFilterProp="label"
        placeholder={placeholder}
        // The label above the control is decoration. A screen reader reads the name from
        // here, and the placeholder is gone as soon as the level holds a value.
        aria-label={placeholder}
        value={value ?? undefined}
        options={options}
        disabled={status.disabled}
        loading={status.loading}
        onChange={onChange}
      />
      {status.message && (
        <div className="cascade-hint">
          <span>{status.message}</span>
          {status.canRetry && (
            <BaseButton
              appearance="subtle"
              ellipses={false}
              className="retry-button"
              onClick={onRetry}>
              Retry
            </BaseButton>
          )}
        </div>
      )}
    </div>
  );
}

interface UsLandgridSelectorProps {
  closeMenu: () => void;
  jumpToPlace: (place: PlaceLocation, fallbackZoom: number) => void;
}

/**
 * Jump to a place named by the US landgrid: a state, then whichever levels that state's
 * survey system has. The region list says which system each state uses, so the levels
 * below the first are the form's answer to give rather than anything chosen here.
 */
export default function UsLandgridSelector({
  closeMenu,
  jumpToPlace
}: UsLandgridSelectorProps) {
  const [errors, setErrors] = useState("");
  const [selection, setSelection] = useState<JumpToLocationSelection>(emptyCascade);
  // Which jump the panel is still waiting on. The panel stays open and editable while a
  // lookup is in flight, so an answer that the panel has moved on from must not be
  // allowed to move the map.
  const latestJumpId = useRef(0);

  const cascade = useJumpToLocationOptions(selection, true);
  const cascadeForm = cascade.form;
  const regionOptions = useMemo(
    () => cascade.regions.options.map(toSelectOption),
    [cascade.regions.options]
  );
  const regionStatus = describeLevel(
    cascade.regions,
    "states",
    true,
    "No states available"
  );

  // One row per level of the chosen region's form. Until a region is chosen there is no
  // form, so there are no levels to draw: which ones follow is the region's answer to give.
  const cascadeLevels = (cascadeForm?.levels ?? [])
    .map((level, index) => ({ level, answered: cascade.levels[index] }))
    // The lists come back one per level the form has a query for. A level with no list
    // behind it is left out, rather than drawn from nothing.
    .filter(({ answered }) => Boolean(answered))
    .map(({ level, answered }) => ({
      level,
      options: answered.options.map(toSelectOption),
      status: describeLevel(
        answered,
        level.plural,
        level.parents.every((parent) => Boolean(selection[parent])),
        level.emptyMessage
      ),
      refetch: answered.refetch
    }));

  const cascadeReady = Boolean(cascadeForm?.canSubmit(selection));

  // A region the region list offered, surveyed by a system this build has no form for.
  // There is nothing to draw under it and nothing Go could do with it, so it is said
  // plainly rather than left as an empty panel.
  const showNoFormHint =
    Boolean(selection.region) &&
    !cascadeForm &&
    !cascade.regions.isFetching &&
    cascade.regions.options.length > 0;

  function commitSelection(newSelection: JumpToLocationSelection) {
    // The selection a lookup in flight is resolving is no longer the one the panel holds,
    // so its answer is retired here the same way a newer press of Go retires it.
    latestJumpId.current++;
    setErrors("");
    setSelection(newSelection);
  }

  function handleRegionChange(val) {
    // A region change is a form change, so nothing below it survives: the level a value
    // belonged to may not even be part of the new form.
    commitSelection({ ...emptyCascade(), region: val ?? null });
  }

  function handleCascadeChange(field: CascadeFieldName, val) {
    const cleared = {};
    fieldsClearedBy(cascadeForm, field).forEach((below) => {
      cleared[below] = null;
    });
    // The cleared levels are spread first, so the level being set always wins even if a
    // future change makes the two overlap.
    commitSelection({
      ...selection,
      ...cleared,
      [field]: val ?? null
    });
  }

  /**
   * Resolves the chosen selection and flies to it. The panel stays open and editable for
   * the whole request, so only the newest jump may move the map or report a failure: an
   * answer that a later press of Go or an edit has left behind would fly back to a place
   * the user is no longer on, and it is that place the next save would record.
   */
  async function jumpToChosenPlace() {
    const jumpId = ++latestJumpId.current;
    setErrors("");
    try {
      const place = await cascadeForm.resolvePlace(selection);
      if (jumpId !== latestJumpId.current) return;

      jumpToPlace(place, cascadeForm.fallbackZoom(selection));
      closeMenu && closeMenu();
    } catch (err) {
      if (jumpId !== latestJumpId.current) return;

      const status = (err as AxiosError)?.response?.status;
      setErrors(
        status === REFERENCE_UNAVAILABLE_STATUS ? LOCATION_UNAVAILABLE : UNABLE_TO_FIND
      );
    }
  }

  return (
    <LandgridContainer>
      <div className="landgrid-cascade">
        <CascadeField
          testId={REGION_TEST_ID}
          placeholder={REGION_LABEL}
          value={selection.region}
          options={regionOptions}
          status={regionStatus}
          onRetry={cascade.regions.refetch}
          onChange={handleRegionChange}
        />
        {showNoFormHint && (
          <div className="cascade-hint">
            <span>{NO_FORM_FOR_REGION}</span>
          </div>
        )}
        {cascadeLevels.map(({ level, options, status, refetch }) => (
          <CascadeField
            key={level.field}
            testId={`jump-${level.field}-select`}
            placeholder={level.label}
            value={selection[level.field]}
            options={options}
            status={status}
            onRetry={refetch}
            onChange={(val) => handleCascadeChange(level.field, val)}
          />
        ))}
      </div>
      <JumpActionRow
        idPrefix="jump"
        canSubmit={cascadeReady}
        onClear={() => commitSelection(emptyCascade())}
        onSubmit={jumpToChosenPlace}
      />
      {errors.length > 0 && (
        <div className="flex-row justify-center">
          <FieldErrorMessage message={errors} />
        </div>
      )}
    </LandgridContainer>
  );
}

const LandgridContainer = styled.div`
  ${jumpPanelStyles}

  .landgrid-cascade {
    display: flex;
    flex-direction: column;
    /* Each label stands in the row it belongs to rather than over the row above, so what
       is left between two rows is only the space that keeps them apart. A stack of five
       reads as one form at this distance, and the panel stays short enough to see at
       once. */
    gap: 4px;
    /* Every row keeps a line for its label whether the label is shown or not, so the top
       row carries a blank one and would start a whole line lower than the other tab's
       first control. The stack is lifted by exactly that one line, which puts the first
       select at the same height as the other tab's and leaves the first label standing in
       space that was empty anyway. The reserved line is what stops the rows below from
       stepping down as the form is filled in, so it stays. */
    margin-top: calc(-1 * var(--field-label-line));
    /* Five stacked selects can outgrow a short window. Only the cascade scrolls, so the
       Go row is left where it is. */
    max-height: 60vh;
    overflow-y: auto;

    .cascade-field {
      width: 100%;
    }
    .cascade-hint {
      display: flex;
      align-items: center;
      gap: 4px;
      padding-top: 2px;
      color: #666;
      font-size: 12px;
    }
    .retry-button {
      height: auto;
      min-width: 0;
      padding: 0;
      font-size: 12px;
      text-decoration: underline;
    }
  }
`;

const CascadeSelect = styled(Select)`
  width: 100%;
`;
